import { createContext, useContext } from "react";

import type { UserSheetCommsTab } from "./types";

// Provided by UserSheetProvider. View preferences (collapse states, resume
// zoom, split panes) live in userSheetViewStore instead.
interface UserSheetContextValue {
  // Present when the sheet was opened from a candidacy; scopes comms and hides
  // the current candidacy from the Other Candidacies tab.
  candidacyId?: string;
  closeCommunications: () => void;
  // Which scope tab the comms panel shows (e.g. Internal vs Candidate). Owned
  // here (not by the panel) so focus mode can save/restore it; undefined until
  // the staffer picks one, so panels fall back to their own default.
  communicationsScope?: string;
  // The scope the most recent openCommunications call requested, if any.
  // Undefined means the caller expressed no preference.
  communicationsRequestScope?: string;
  // In context so custom comms panels can read/switch it without prop threading.
  communicationsTab: UserSheetCommsTab;
  // Bumped on every openCommunications call so panels can react (e.g. jump to
  // the candidate scope) even when the tab value itself didn't change.
  communicationsRequestCount: number;
  communicationsWidthPercent: number;
  // Tab content shifts right when the comms rail opens, so tabs can't hardcode it.
  contentPaddingClassName: string;
  // False when the host mounted no comms panel — the header handle hides and
  // openCommunications no-ops rather than expanding an empty rail.
  hasCommunications: boolean;
  isCommunicationsOpen: boolean;
  // The application review portal shows the candidate as-is: no profile edits,
  // no contact actions, no adding records. Reviewers only decide.
  isReadOnly: boolean;
  // Scopes the profile query; kept here so children can read the same cache entry.
  jobId?: string;
  // Only set on candidacy-backed sheets — lets the header offer "add email"
  // when the candidate has none, without every consumer wiring its own dialog.
  onEditProfile?: () => void;
  // Opens the rail on `tab`. Pass `scope` when the tab lives outside the
  // Candidate rail — omitting it keeps the header-action default.
  openCommunications: (tab: UserSheetCommsTab, scope?: string) => void;
  setCommunicationsScope: (scope: string | undefined) => void;
  setCommunicationsWidthPercent: (widthPercent: number) => void;
  workerId: number;
}

export const UserSheetContext = createContext<UserSheetContextValue | null>(
  null,
);

export const useUserSheet = () => {
  const context = useContext(UserSheetContext);
  if (!context) {
    throw new Error("useUserSheet must be used inside a UserSheetProvider");
  }
  return context;
};
